import { Component, ElementRef, Renderer2, effect, inject, input, type OnDestroy, type OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { FindReplaceService } from "./find-replace.service";
import { TRANSFORMER_STATE } from "../abstract-transformer.service";

@Component({
  template: ``,
  styleUrls: ["./find-replace-cell-renderer.component.scss"],
})
export class FindReplaceCellRendererComponent implements OnInit, OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private service = inject(FindReplaceService);

  readonly value = input<any>();

  private subscriptions: Subscription[] = [];

  private matchPattern: RegExp | null = null;
  private escapeRegex = /[-\/\\^$*+?.()|[\]{}]/g;

  constructor() {
    // Re-renders whenever the cell's value input changes (replaces the old @Input setter + ngOnChanges).
    effect(() => {
      this.value();
      this.render();
    });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.service.findText$.subscribe(() => {
        this.createFindPattern();
        this.render();
      }),
      this.service.state$.subscribe(() => {
        this.createFindPattern();
        this.render();
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /** Elastic uses fuzzy pattern matching that we will try to replicate in the highlighting */
  private createFindPattern() {
    // find 'tokens' in the find text:
    let ft = this.service.state === TRANSFORMER_STATE.DONE ? this.service.replaceText : this.service.findText;
    if (ft) {
      ft = ft.trim().normalize();
    }
    this.matchPattern = null;
    if (ft && ft.length) {
      const escaped = this.escapeFindText(ft);
      this.matchPattern = new RegExp(escaped, "gi");
      // const matches = ft.match(/([\w\'/]+)/gi);
      // if (matches) {
      //   const pattern = matches.join('[\\W]+');
      //   this.matchPattern = new RegExp(pattern, 'gi');
      // }
    }
  }

  private escapeFindText(s: string): string {
    return s.replace(this.escapeRegex, "\\$&");
  }

  private render() {
    const native = <HTMLElement>this.el.nativeElement;
    while (native.firstChild) {
      native.removeChild(native.firstChild);
    }

    const findText = this.service.findText.normalize();
    const text: string = (<string>this.value()).normalize();

    if (!findText || !this.matchPattern) {
      this.renderer.appendChild(native, this.renderer.createText(text));
      return;
    }

    let index = 0,
      prevIndex = 0;
    const matches = text.match(this.matchPattern) || [];
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      index = text.indexOf(matches[i], prevIndex);
      const start = text.substring(prevIndex, index);
      if (start.length) {
        this.renderer.appendChild(native, this.renderer.createText(start));
      }
      const middle = this.highlight(match);
      this.renderer.appendChild(native, middle);
      index += match.length;
      prevIndex = index;
    }
    const remainder = prevIndex > 0 ? text.substring(prevIndex) : text;
    this.renderer.appendChild(native, this.renderer.createText(remainder));
  }

  private highlight(matched: string): HTMLElement {
    const span: HTMLSpanElement = this.renderer.createElement("span");
    this.renderer.addClass(span, this.service.state === TRANSFORMER_STATE.DONE ? "replaced" : "found");
    this.renderer.appendChild(span, this.renderer.createText(matched));
    return span;
  }
}
